import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';

// Banner image spec (Figma "Size" annotation): 21:9, cover-crop, min 1200×514px.
const ASPECT = 21 / 9;
const MIN_W = 1200;
const MIN_H = 514;
// A "perfect" 1200×514 image isn't exactly 21:9 (21:9 of 514 = 1199.3px), so its
// full 21:9 crop rounds to 1199px. Allow a few px of slack so it isn't rejected.
const MIN_TOL = 4;

interface Props {
  file: File;
  onCancel: () => void;
  onApply: (croppedFile: File) => void;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function getCroppedFile(imageSrc: string, crop: Area, name: string): Promise<File> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(crop.width);
  canvas.height = Math.round(crop.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
  const blob: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Crop failed'))), 'image/jpeg', 0.92)
  );
  return new File([blob], name.replace(/\.[^.]+$/, '') + '-banner.jpg', { type: 'image/jpeg' });
}

export const BannerCropModal: React.FC<Props> = ({ file, onCancel, onApply }) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixels, setCropPixels] = useState<Area | null>(null);
  const [error, setError] = useState('');
  const [tooSmall, setTooSmall] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const reader = new FileReader();
    reader.onload = async () => {
      const src = reader.result as string;
      const img = await loadImage(src);
      if (img.naturalWidth < MIN_W || img.naturalHeight < MIN_H) {
        setTooSmall(true);
        setError(`Image is ${img.naturalWidth}×${img.naturalHeight}px — minimum is ${MIN_W}×${MIN_H}px.`);
      }
      setImageSrc(src);
    };
    reader.readAsDataURL(file);
  }, [file]);

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCropPixels(areaPixels);
  }, []);

  const apply = async () => {
    if (!imageSrc || !cropPixels) return;
    if (Math.round(cropPixels.width) < MIN_W - MIN_TOL || Math.round(cropPixels.height) < MIN_H - MIN_TOL) {
      setError(`Selected area is ${Math.round(cropPixels.width)}×${Math.round(cropPixels.height)}px — zoom out so it stays at least ${MIN_W}×${MIN_H}px.`);
      return;
    }
    try {
      setBusy(true);
      const cropped = await getCroppedFile(imageSrc, cropPixels, file.name);
      onApply(cropped);
    } catch (e: any) {
      setError(e?.message || 'Failed to crop image');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-medium text-gray-900">Crop banner image</h3>
          <p className="mt-1 text-xs text-gray-500">Fixed 21:9 frame · pan &amp; zoom · min. {MIN_W}×{MIN_H}px</p>
        </div>

        <div className="px-6 py-4">
          <div className="relative h-[320px] w-full overflow-hidden rounded-md bg-gray-900">
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={ASPECT}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                objectFit="contain"
              />
            )}
          </div>

          <div className="mt-4 flex items-center gap-3">
            <span className="text-sm text-gray-600">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={e => setZoom(Number(e.target.value))}
              className="flex-1 accent-alps-primary"
            />
          </div>

          {error && (
            <p className={`mt-3 text-sm ${tooSmall ? 'text-red-600' : 'text-amber-600'}`}>{error}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={busy || tooSmall || !cropPixels}
            className="rounded-md bg-alps-primary px-4 py-2 text-sm font-medium text-white hover:bg-alps-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Applying…' : 'Apply crop'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
